import React, { useState, useEffect, useRef } from 'react';
import { getShifts, createShift, deleteShift, getAttendanceUsers } from './attendanceApi';
import { format, addDays, getDay, parseISO, isBefore, isAfter, startOfDay, endOfDay, differenceInMinutes, areIntervalsOverlapping } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarRange, Banknote, Trash2, Plus, Clock, User as UserIcon, Coffee, X, History, LayoutGrid, List, FileText, Share2 } from 'lucide-react';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/useAuthStore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function ShiftManager() {
  const { user: currentUser } = useAuthStore();
  const isReadOnly = currentUser?.role === 'locutor';
  const formRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPastShifts, setShowPastShifts] = useState(false);
  const [viewMode, setViewMode] = useState('gantt'); 
  const [now, setNow] = useState(new Date());
  
  const [editingId, setEditingId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [amount, setAmount] = useState('0');
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]);
  const [breaks, setBreaks] = useState([]); 

  const [searchUser, setSearchUser] = useState('');
  const [filterStart, setFilterStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filterEnd, setFilterEnd] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));

  const hoursGrid = Array.from({ length: 16 }, (_, i) => i + 7); // 07:00 a 22:00

  const daysOfWeek = [
    { id: 1, label: 'Lu' }, { id: 2, label: 'Ma' }, { id: 3, label: 'Mi' },
    { id: 4, label: 'Ju' }, { id: 5, label: 'Vi' }, { id: 6, label: 'Sa' }, { id: 0, label: 'Do' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
      const [u, s] = await Promise.all([getAttendanceUsers(), getShifts()]);
      setUsers(u);
      setShifts(s);
    } catch (error) { console.error(error); }
  };

  useEffect(() => { fetchData(); }, []);

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ''}` : `${m}m`;
  };

  const calculateTotalDuration = (startT, endT, breakList) => {
    const dummyStart = new Date(`2000-01-01T${startT}`);
    let dummyEnd = new Date(`2000-01-01T${endT}`);
    if (isBefore(dummyEnd, dummyStart)) dummyEnd = addDays(dummyEnd, 1);

    let total = differenceInMinutes(dummyEnd, dummyStart);
    breakList.forEach(b => {
      if (b.start && b.end) {
        let bS = new Date(`2000-01-01T${b.start}`);
        let bE = new Date(`2000-01-01T${b.end}`);
        if (isBefore(bE, bS)) bE = addDays(bE, 1);
        total -= differenceInMinutes(bE, bS);
      }
    });
    return total;
  };

  const resetForm = () => {
    if (isReadOnly) return;
    setEditingId(null);
    setSelectedUserId('');
    setAmount('0');
    setBreaks([]);
  };

  const handleDelete = async (id) => {
    if (isReadOnly) return;
    if (!window.confirm('¿Borrar turno definitivamente?')) return;
    try {
        await deleteShift(id);
        toast.success("Turno eliminado");
        fetchData();
    } catch (error) {
        toast.error("Error al eliminar");
    }
  };

  const handleEdit = (shift) => {
    if (isReadOnly) return;
    setEditingId(shift.id);
    setSelectedUserId(shift.user_id.toString());
    const sDT = parseISO(shift.start_time);
    const eDT = parseISO(shift.end_time);
    setStartDate(format(sDT, 'yyyy-MM-dd'));
    setStartTime(format(sDT, 'HH:mm'));
    setEndTime(format(eDT, 'HH:mm'));
    setAmount(shift.amount.toString());
    setBreaks(shift.breaks?.map(b => ({
      start: format(parseISO(b.start_time), 'HH:mm'),
      end: format(parseISO(b.end_time), 'HH:mm'),
      description: b.description || ''
    })) || []);

    if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    setLoading(true);
    try {
      const createShiftData = (date) => {
        const startDT = new Date(`${date}T${startTime}`);
        let endDT = new Date(`${date}T${endTime}`);
        if (isBefore(endDT, startDT)) endDT = addDays(endDT, 1);
        
        return {
            user_id: Number(selectedUserId),
            start_time: startDT.toISOString(),
            end_time: endDT.toISOString(),
            amount: Number(amount),
            breaks: breaks.map(b => {
                let bS = new Date(`${date}T${b.start}`);
                let bE = new Date(`${date}T${b.end}`);
                if (isBefore(bE, bS)) bE = addDays(bE, 1);
                return { start_time: bS.toISOString(), end_time: bE.toISOString(), description: b.description };
            })
        };
      };

      if (editingId) {
        await deleteShift(editingId);
        await createShift(createShiftData(startDate));
      } else {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        let current = start;
        while (isBefore(current, addDays(end, 1))) {
          if (selectedDays.includes(getDay(current))) {
            await createShift(createShiftData(format(current, 'yyyy-MM-dd')));
          }
          current = addDays(current, 1);
        }
      }
      fetchData();
      resetForm();
      toast.success("Turnos actualizados");
    } catch (error) { toast.error('Error al guardar'); } finally { setLoading(false); }
  };

  const shareByWhatsApp = () => {
    if (!searchUser) return toast.info("Selecciona un locutor para enviar turnos");
    const user = users.find(u => u.id === Number(searchUser));
    const sorted = [...filteredShifts].sort((a,b) => a.start_time.localeCompare(b.start_time));
    
    let text = `┏━━━━━━━━━━━━━━━━━━━━━━━━┓\n`;
    text += `   📅 *CONTROL DE TURNOS*\n`;
    text += `┗━━━━━━━━━━━━━━━━━━━━━━━━┛\n`;
    text += `👤 *Personal:* ${user?.name || user?.email}\n`;
    text += `🗓️ *Periodo:* ${format(parseISO(filterStart), 'dd/MM')} al ${format(parseISO(filterEnd), 'dd/MM')}\n`;
    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    sorted.forEach(s => {
        const sDT = parseISO(s.start_time);
        const eDT = parseISO(s.end_time);
        const spansDays = format(sDT, 'yyyy-MM-dd') !== format(eDT, 'yyyy-MM-dd');
        
        text += `✅ *${format(sDT, 'EEEE dd/MM', { locale: es }).toUpperCase()}*\n`;
        text += `⏰ ${format(sDT, 'HH:mm')} - ${format(eDT, 'HH:mm')}${spansDays ? ' (+1)' : ''}\n`;
        text += `📍 Estado: Programado\n\n`;
    });

    const totalMinutes = sorted.reduce((acc, s) => acc + differenceInMinutes(parseISO(s.end_time), parseISO(s.start_time)), 0);
    const hours = Math.floor(totalMinutes / 60);

    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    text += `📊 *Resumen:* ${sorted.length} Turnos\n`;
    text += `🕒 *Total Horas:* ${hours}h\n`;
    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    text += `_Generado por Transportes Emilia_`;

    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const downloadPDF = () => {
    if (!searchUser) return toast.info("Selecciona un locutor para generar el PDF");
    const user = users.find(u => u.id === Number(searchUser));
    const sorted = [...filteredShifts].sort((a,b) => a.start_time.localeCompare(b.start_time));

    const doc = new jsPDF();
    
    const addContent = (logoImg = null) => {
        doc.setFontSize(22);
        doc.setTextColor(0, 51, 102); 
        doc.text("CONTROL DE ASISTENCIA", 14, 22);
        
        if (logoImg) {
            try {
                doc.addImage(logoImg, 'PNG', 160, 10, 30, 30);
            } catch (e) { console.warn("Error al insertar logo en PDF"); }
        }

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Empresa: Transportes Emilia`, 14, 30);
        doc.text(`Fecha Emisión: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 35);
        
        doc.setFillColor(245, 247, 250);
        doc.rect(14, 45, 182, 20, 'F');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`PERSONAL: ${user?.name || user?.email}`, 20, 53);
        doc.text(`PERIODO: ${format(parseISO(filterStart), 'dd/MM/yyyy')} al ${format(parseISO(filterEnd), 'dd/MM/yyyy')}`, 20, 60);

        let totalMinutesAccumulated = 0;

        const tableRows = sorted.map(s => {
            const sDT = parseISO(s.start_time);
            const eDT = parseISO(s.end_time);
            
            let diff = differenceInMinutes(eDT, sDT);
            const spansDays = format(sDT, 'yyyy-MM-dd') !== format(eDT, 'yyyy-MM-dd');
            
            // DESCONTAR COLACIÓN DE LA DURACIÓN Y DEL TOTAL
            if (s.breaks) {
                s.breaks.forEach(b => {
                    diff -= differenceInMinutes(parseISO(b.end_time), parseISO(b.start_time));
                });
            }
            totalMinutesAccumulated += diff;

            const breakSummary = s.breaks && s.breaks.length > 0 
                ? s.breaks.map(b => `${format(parseISO(b.start_time), 'HH:mm')} - ${format(parseISO(b.end_time), 'HH:mm')}`).join(', ')
                : 'Sin colación';
            
            return [
                format(sDT, 'EEEE dd/MM/yyyy', { locale: es }),
                format(sDT, 'HH:mm'),
                `${format(eDT, 'HH:mm')}${spansDays ? ' (+1)' : ''}`,
                formatDuration(diff),
                breakSummary,
                new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(s.amount)
            ];
        });

        autoTable(doc, {
            startY: 75,
            head: [['Día / Fecha', 'Entrada', 'Salida', 'Efectivo', 'Colaciones', 'Monto']],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [0, 51, 102], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: { 5: { halign: 'right' } }
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        const totalAmount = sorted.reduce((acc, s) => acc + s.amount, 0);
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`TOTAL HORAS EFECTIVAS: ${formatDuration(totalMinutesAccumulated)}`, 196, finalY, { align: 'right' });
        doc.text(`TOTAL A PAGAR: ${new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(totalAmount)}`, 196, finalY + 7, { align: 'right' });

        doc.save(`Turnos_${user?.name || 'Personal'}_${format(new Date(), 'yyyyMMdd')}.pdf`);
        toast.success("PDF generado con éxito");
    };

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = window.location.origin + '/logoe.png';
    img.onload = () => addContent(img);
    img.onerror = () => {
        console.warn("Logo falló, probando ruta relativa");
        const img2 = new Image();
        img2.src = '/logoe.png';
        img2.onload = () => addContent(img2);
        img2.onerror = () => addContent(null);
    };
  };

  const filteredShifts = shifts.filter(s => {
    const sS = parseISO(s.start_time);
    const sE = parseISO(s.end_time);
    const fS = startOfDay(parseISO(filterStart));
    const fE = endOfDay(parseISO(filterEnd));
    const matchesUser = !searchUser || s.user_id === Number(searchUser);
    const touchesRange = isBefore(sS, fE) && isAfter(sE, fS);
    const matchesHistory = showPastShifts || isAfter(sE, startOfDay(now));
    return matchesUser && touchesRange && matchesHistory;
  });

  const groupedShifts = filteredShifts.reduce((acc, s) => {
    const start = parseISO(s.start_time);
    const end = parseISO(s.end_time);
    let curr = startOfDay(start);
    const last = startOfDay(end);
    while (curr <= last) {
      const d = format(curr, 'yyyy-MM-dd');
      if (d >= filterStart && d <= filterEnd) {
          if (!acc[d]) acc[d] = [];
          acc[d].push(s);
      }
      curr = addDays(curr, 1);
    }
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedShifts).sort((a, b) => a.localeCompare(b));

  const calculatePosition = (timeStr, blockDate) => {
    const time = new Date(timeStr);
    const bStart = startOfDay(blockDate);
    const gridStartHour = 7;
    let hours = time.getHours();
    let minutes = time.getMinutes();
    if (isBefore(time, addDays(bStart, 0).setHours(gridStartHour))) {
        hours = gridStartHour;
        minutes = 0;
    }
    const minFromStart = (hours - gridStartHour) * 60 + minutes;
    return Math.max(0, (minFromStart / (16 * 60)) * 100);
  };

  const getVisualBoundaries = (s, blockDate, isGantt = false) => {
    const start = parseISO(s.start_time);
    const end = parseISO(s.end_time);
    const bStart = startOfDay(blockDate);
    const bEnd = endOfDay(blockDate);
    const gridStartHour = isGantt ? 0 : 7;
    const gridDurationHours = isGantt ? 24 : 16;
    const gS = new Date(bStart); gS.setHours(gridStartHour, 0, 0, 0);
    const gE = new Date(bStart); gE.setHours(gridStartHour + gridDurationHours, 0, 0, 0);
    const vS = isBefore(start, gS) ? gS : start;
    const vE = isAfter(end, gE) ? gE : end;
    const startMin = (vS.getHours() - gridStartHour) * 60 + vS.getMinutes();
    const durationMin = differenceInMinutes(vE, vS);
    return {
        left: (startMin / (gridDurationHours * 60)) * 100,
        width: (durationMin / (gridDurationHours * 60)) * 100,
        isContinuing: isBefore(start, bStart),
        willContinue: isAfter(end, bEnd),
        vS, vE
    };
  };

  const organizeShiftsInColumns = (dayShifts, blockDate) => {
    if (!dayShifts) return [];
    const sorted = [...dayShifts].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const columns = [];
    sorted.forEach(shift => {
      let placed = false;
      const boundaries = getVisualBoundaries(shift, blockDate);
      const vInterval = {
          start: new Date(blockDate).setHours(7 + (boundaries.left / 100) * 16, 0, 0, 0),
          end: new Date(blockDate).setHours(7 + ((boundaries.left + boundaries.width) / 100) * 16, 0, 0, 0)
      };
      for (let i = 0; i < columns.length; i++) {
        const last = columns[i][columns[i].length - 1];
        const lB = getVisualBoundaries(last, blockDate);
        const lVInterval = {
            start: new Date(blockDate).setHours(7 + (lB.left / 100) * 16, 0, 0, 0),
            end: new Date(blockDate).setHours(7 + ((lB.left + lB.width) / 100) * 16, 0, 0, 0)
        };
        if (!areIntervalsOverlapping(lVInterval, vInterval)) {
          columns[i].push(shift);
          shift.colIndex = i;
          placed = true;
          break;
        }
      }
      if (!placed) { columns.push([shift]); shift.colIndex = columns.length - 1; }
    });
    sorted.forEach(shift => { shift.totalCols = columns.length; });
    return sorted;
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
      {!isReadOnly && (
        <div className="xl:col-span-1" ref={formRef}>
          <div className={`bg-white border ${editingId ? 'border-blue-500 shadow-lg shadow-blue-500/10' : 'border-slate-200'} rounded-[2rem] p-8 shadow-sm`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-800 uppercase flex items-center gap-2">
                <CalendarRange size={20} className="text-blue-600" />
                {editingId ? 'Editar' : 'Programar'}
              </h2>
              {editingId && <button onClick={resetForm} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>}
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              <div className="relative">
                <UserIcon className="absolute left-4 top-4 text-slate-400" size={18} />
                <select required value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 pl-12 border border-slate-200 focus:border-blue-500 outline-none shadow-sm transition-all">
                  <option value="">Locutor...</option>
                  {users.filter(u => (u.role === 'locutor' || u.email?.toLowerCase() === 'maria.soledad.fernandezi@gmail.com') && u.is_active).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </div>

              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 border border-slate-200 focus:border-blue-500 outline-none shadow-sm" />
              
              {!editingId && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Repetir hasta</p>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 border border-slate-200 focus:border-blue-500 outline-none shadow-sm" />
                </div>
              )}

              {!editingId && (
                <div className="flex gap-2">
                  {daysOfWeek.map(d => (
                    <button key={d.id} type="button" onClick={() => setSelectedDays(prev => prev.includes(d.id) ? prev.filter(x => x !== d.id) : [...prev, d.id])} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${selectedDays.includes(d.id) ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>{d.label}</button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Clock className="absolute left-4 top-4 text-slate-400" size={18} />
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 border border-slate-200 focus:border-blue-500 outline-none shadow-sm" />
                </div>
                <div className="relative">
                  <Clock className="absolute left-4 top-4 text-slate-400" size={18} />
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 border border-slate-200 focus:border-blue-500 outline-none shadow-sm" />
                </div>
              </div>

              <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest text-center">
                Duración: {formatDuration(calculateTotalDuration(startTime, endTime, breaks))}
              </p>

              <div className="relative">
                <Banknote className="absolute left-4 top-4 text-slate-400" size={18} />
                <input type="number" placeholder="Monto..." value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-slate-50 text-slate-800 rounded-2xl p-4 pl-12 border border-slate-200 focus:border-blue-500 outline-none shadow-sm" />
              </div>

              <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 space-y-4 shadow-inner">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Coffee size={18} className="text-amber-600" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Colaciones</span>
                  </div>
                  <button type="button" onClick={() => setBreaks([...breaks, { start: '13:00', end: '14:00', description: 'Colación' }])} className="p-2 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-600 transition-all"><Plus size={16} /></button>
                </div>
                {breaks.map((b, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-4 border border-slate-200 space-y-2">
                    <div className="flex items-center gap-2">
                        <Coffee size={18} className="text-amber-600" />
                        <input type="text" value={b.description} onChange={(e) => { const nb = [...breaks]; nb[idx].description = e.target.value; setBreaks(nb); }} className="w-full bg-transparent border-none text-xs font-black outline-none text-slate-800" />
                        <button type="button" onClick={() => setBreaks(breaks.filter((_, i) => i !== idx))} className="text-slate-300 hover:text-rose-500 transition-colors"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <Clock className="absolute left-2 top-2 text-slate-400" size={14} />
                        <input type="time" value={b.start} onChange={(e) => { const nb = [...breaks]; nb[idx].start = e.target.value; setBreaks(nb); }} className="w-full bg-slate-50 pl-8 p-2 rounded-xl text-xs border border-slate-100 outline-none font-bold" />
                      </div>
                      <div className="relative">
                        <Clock className="absolute left-2 top-2 text-slate-400" size={14} />
                        <input type="time" value={b.end} onChange={(e) => { const nb = [...breaks]; nb[idx].end = e.target.value; setBreaks(nb); }} className="w-full bg-slate-50 pl-8 p-2 rounded-xl text-xs border border-slate-100 outline-none font-bold" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white font-black py-5 rounded-2xl uppercase tracking-widest shadow-lg hover:bg-blue-700 transition-all">
                {loading ? '...' : (editingId ? 'Actualizar' : 'Generar')}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className={`${isReadOnly ? 'xl:col-span-4' : 'xl:col-span-3'} space-y-6`}>
        <div className="bg-white border border-slate-200 p-6 rounded-[2rem] shadow-sm flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img src="/logoe.png" alt="Logo" className="h-12 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                <button onClick={() => setViewMode('gantt')} className={`p-2 rounded-lg transition-all flex items-center gap-2 text-[10px] font-black uppercase ${viewMode === 'gantt' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400'}`}><LayoutGrid size={16} /> Gantt</button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all flex items-center gap-2 text-[10px] font-black uppercase ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400'}`}><List size={16} /> Agenda</button>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
             <select value={searchUser} onChange={(e) => setSearchUser(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 text-[10px] font-bold outline-none">
               <option value="">Todos</option>
               {users.filter(u => (u.role === 'locutor' || u.email?.toLowerCase() === 'maria.soledad.fernandezi@gmail.com')).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
             </select>
             <input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 text-[10px] font-bold outline-none" />
             <input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 text-[10px] font-bold outline-none" />
             {!isReadOnly && (
               <div className="flex gap-2">
                 <button onClick={shareByWhatsApp} className="flex items-center gap-2 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-xl px-4 py-2 text-[10px] font-black uppercase hover:bg-emerald-600 hover:text-white transition-all">
                   <Share2 size={14} /> WhatsApp
                 </button>
                 <button onClick={downloadPDF} className="flex items-center gap-2 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl px-4 py-2 text-[10px] font-black uppercase hover:bg-rose-600 hover:text-white transition-all">
                   <FileText size={14} /> PDF
                 </button>
               </div>
             )}
          </div>
          <button onClick={() => setShowPastShifts(!showPastShifts)} className="text-xs font-bold text-slate-400 flex items-center gap-2"><History size={16}/> {showPastShifts ? 'Ocultar' : 'Ver'} Historial</button>
        </div>

        <div className="space-y-12">
          {sortedDates.map(d => {
            const blockDate = parseISO(d);
            const dayShifts = organizeShiftsInColumns(groupedShifts[d], blockDate);
            return (
              <div key={d} className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-xl animate-in fade-in zoom-in-95">
                <div className="bg-slate-50/50 p-8 border-b border-slate-100 font-black text-slate-700 capitalize text-xl">{format(blockDate, "EEEE d MMMM", { locale: es })}</div>

                {viewMode === 'gantt' ? (
                  <div className="p-8 relative min-h-[140px]">
                    <div className="absolute top-4 left-8 right-8 flex justify-between text-[8px] font-black text-slate-300 uppercase tracking-tighter border-b border-slate-100 pb-2">
                      {[...Array(25)].map((_, i) => (<span key={i}>{String(i).padStart(2, '0')}</span>))}
                    </div>
                    <div className="pt-10 space-y-3">
                      {dayShifts.map(s => {
                        const user = users.find(u => u.id === s.user_id);
                        const b = getVisualBoundaries(s, blockDate, true);
                        const startMinGlobal = new Date(s.start_time).getHours() * 60 + new Date(s.start_time).getMinutes();
                        const durationGlobal = differenceInMinutes(parseISO(s.end_time), parseISO(s.start_time));

                        return (
                          <div key={`${s.id}-${d}`} className="relative h-14 group">
                            <div onClick={() => handleEdit(s)} className={`absolute inset-y-0 rounded-2xl bg-blue-50 border border-blue-200 flex items-center px-5 overflow-hidden transition-all ${isReadOnly ? '' : 'cursor-pointer hover:border-blue-400'}`} style={{ left: `${b.left}%`, width: `${b.width}%` }} title={`Duración Total: ${durationGlobal}m (Inicia min: ${startMinGlobal})`}>
                              {s.breaks && s.breaks.map((br, bi) => {
                                 const brStart = parseISO(br.start_time);
                                 const brEnd = parseISO(br.end_time);
                                 if (format(brStart, 'yyyy-MM-dd') === d) {
                                     const brStartMin = brStart.getHours() * 60 + brStart.getMinutes();
                                     const brEndMin = brEnd.getHours() * 60 + brEnd.getMinutes();
                                     const brLeft = ((brStartMin - (b.left/100*1440)) / (b.width/100*1440)) * 100;
                                     const brWidth = ((brEndMin - brStartMin) / (b.width/100*1440)) * 100;
                                     return (
                                       <div key={bi} className="absolute inset-y-0 bg-amber-200/40 flex items-center justify-center border-x border-amber-300/30" style={{ left: `${brLeft}%`, width: `${brWidth}%` }}>
                                         <Coffee size={10} className="text-amber-700 opacity-50" />
                                       </div>
                                     );
                                 }
                                 return null;
                              })}
                              <span className="text-xs font-black text-slate-700 relative z-10">{user?.name || user?.email} {(b.isContinuing || b.willContinue) && '📅'}</span>
                            </div>
                            {!isReadOnly && <button onClick={() => handleDelete(s.id)} className="absolute -right-2 top-1/2 -translate-y-1/2 p-3 bg-white text-rose-500 rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-xl z-50 border border-rose-100"><Trash2 size={18} /></button>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="relative flex p-4" style={{ height: '800px' }}>
                    <div className="w-16 flex flex-col border-r border-slate-100">
                      {hoursGrid.map(h => (<div key={h} className="relative flex-1"><span className="absolute -top-3 right-3 text-[10px] font-black text-slate-400">{String(h).padStart(2, '0')}:00</span></div>))}
                    </div>
                    <div className="flex-1 relative bg-slate-50/20">
                      {hoursGrid.map(h => (<div key={h} className="absolute left-0 right-0 border-b border-slate-100" style={{ top: `${((h-7)/16)*100}%`, height: `${(1/16)*100}%` }}></div>))}
                      {dayShifts.map((s) => {
                        const user = users.find(u => u.id === s.user_id);
                        const b = getVisualBoundaries(s, blockDate);
                        const width = 95 / s.totalCols;
                        const left = s.colIndex * width;

                        return (
                          <div key={`${s.id}-${d}`} onClick={() => handleEdit(s)} className={`absolute rounded-xl shadow-lg border-l-[6px] border-l-rose-500 overflow-hidden transition-all group ${isReadOnly ? '' : 'cursor-pointer hover:scale-[1.02]'}`} style={{ top: `${b.left}%`, height: `${b.width}%`, left: `${left}%`, width: `${width - 1}%`, backgroundColor: '#8b5cf6', zIndex: 10 + s.colIndex }}>
                            <div className="p-3 text-white">
                              <p className="font-black text-[10px] leading-tight m-0 line-clamp-2">{user?.name || user?.email}</p>
                              <p className="text-[9px] font-bold opacity-80 mt-1">
                                {format(parseISO(s.start_time), 'HH:mm')} - {format(parseISO(s.end_time), 'HH:mm')}
                                {(b.isContinuing || b.willContinue) && <span className="ms-1">📅</span>}
                              </p>
                              {s.breaks && s.breaks.map((br, bi) => {
                                 const brStart = parseISO(br.start_time);
                                 const brEnd = parseISO(br.end_time);
                                 if (format(brStart, 'yyyy-MM-dd') === d) {
                                     const brSMin = brStart.getHours() * 60 + brStart.getMinutes();
                                     const brEMin = brEnd.getHours() * 60 + brEnd.getMinutes();
                                     const totalGridMin = 16 * 60;
                                     const topPos = (( (brSMin - 7*60) / totalGridMin ) * 100) - b.left;
                                     const heightPos = (differenceInMinutes(brEnd, brStart) / totalGridMin) * 100;
                                     
                                     return (
                                       <div key={bi} className="absolute left-0 right-0 bg-white/20 flex items-center justify-center border-y border-white/10" style={{ top: `${(differenceInMinutes(brStart, b.vS) / differenceInMinutes(b.vE, b.vS)) * 100}%`, height: `${(differenceInMinutes(brEnd, brStart) / differenceInMinutes(b.vE, b.vS)) * 100}%` }} title={`Break: ${brSMin} a ${brEMin} (Top:${topPos}, Height:${heightPos})`}>
                                         <Coffee size={12} className="text-white/60" />
                                       </div>
                                     );
                                 }
                                 return null;
                              })}
                              {!isReadOnly && <button onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} className="absolute right-1 bottom-1 p-1 bg-white/20 hover:bg-rose-500 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={10} /></button>}
                            </div>
                          </div>
                        );
                      })}
                      {format(now, 'yyyy-MM-dd') === d && (
                        <div className="absolute left-0 right-0 z-50 flex items-center" style={{ top: `${calculatePosition(now.toISOString(), blockDate)}%` }}>
                          <div className="w-2 h-2 rounded-full bg-rose-500 -ml-1"></div>
                          <div className="flex-1 h-[2px] bg-rose-500"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
